import React, { useEffect, useState } from 'react';
import ConversionMetrics from '../components/reporting/ConversionMetrics';
import JobSearchGuidance from '../components/reporting/JobSearchGuidance';
import { motion } from 'framer-motion';
import { 
  CalendarIcon, 
  BriefcaseIcon, 
  ChartBarIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import metricsApi, { MetricsData } from '../services/api/metrics';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  color: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, icon: Icon, color }) => (
  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
    <div className="flex items-center space-x-3">
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-sm text-gray-600">{title}</p>
        <p className="text-xl font-bold">
          {typeof value === 'number' && value > 100 
            ? formatPercentage(value)
            : value}
        </p>
      </div>
    </div>
  </div>
);

// Update the formatPercentage function
const formatPercentage = (value: number): string => {
  // Ensure the value is between 0 and 100
  const normalizedValue = Math.min(100, Math.max(0, value));
  // Round to one decimal place if not a whole number
  return Number.isInteger(normalizedValue) 
    ? `${normalizedValue}%`
    : `${normalizedValue.toFixed(1)}%`;
};

const ReportingPage: React.FC = () => {
  const [metricsData, setMetricsData] = useState<MetricsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [searchStats, setSearchStats] = useState({
    applicationCount: 0,
    responseRate: 0,
    mostCommonRoles: [] as string[],
    averageResponseTime: 0,
    monthlyApplications: [] as { month: string; count: number }[],
    applicationsByIndustry: [
      { industry: 'Technology', percentage: 45 },
      { industry: 'Finance', percentage: 25 },
      { industry: 'Healthcare', percentage: 15 },
      { industry: 'Education', percentage: 10 },
      { industry: 'Other', percentage: 5 }
    ]
  });

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setIsLoading(true);
        const metricsData = await metricsApi.getMetrics();
        
        setMetricsData(metricsData);
        
        // Update searchStats with the metrics data
        setSearchStats(prevStats => ({
          ...prevStats,
          applicationCount: metricsData.total_applications,
          responseRate: Math.round(metricsData.response_rate),
          mostCommonRoles: metricsData.target_positions.map(pos => pos.position).slice(0, 5),
          averageResponseTime: Math.round(metricsData.average_response_time * 10) / 10,
          monthlyApplications: metricsData.monthly_applications,
        }));
        
      } catch (error) {
        console.error('Error fetching metrics:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMetrics();
  }, []);

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const calculateAdditionalMetrics = (data: MetricsData) => {
    const interviewToOffer = data.offers > 0 ? 
      ((data.offers / data.interviews) * 100).toFixed(1) : 0;
    
    const avgApplicationsPerMonth = data.monthly_applications.length > 0 ?
      Math.round(data.total_applications / data.monthly_applications.length) : 0;
    
    const mostActiveMonth = data.monthly_applications.reduce((max, curr) => 
      curr.count > max.count ? curr : max, 
      { month: '', count: 0 }
    );

    return {
      interviewToOffer,
      avgApplicationsPerMonth,
      mostActiveMonth
    };
  };

  if (isLoading || !metricsData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  const additionalMetrics = calculateAdditionalMetrics(metricsData);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 pt-32 pb-12">
        <motion.header 
          className="text-center mb-10"
          initial={fadeIn.initial}
          animate={fadeIn.animate}
          transition={fadeIn.transition}
        >
          <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600 mb-3">
            Your Career Command Center
          </h1>
          <p className="text-gray-600 text-lg">
            Turn Your Job Search Data into Career-Defining Opportunities
          </p>
        </motion.header>

        {/* Top Row - Key Metrics */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6"
          initial={fadeIn.initial}
          animate={fadeIn.animate}
          transition={{ ...fadeIn.transition, delay: 0.2 }}
        >
          <MetricCard
            title="Applications Launched"
            value={metricsData.total_applications}
            icon={BriefcaseIcon}
            color="bg-blue-50 text-blue-600"
          />
          <MetricCard
            title="Interview Win Rate"
            value={`${Math.round(metricsData.application_success_rate)}%`}
            icon={ArrowTrendingUpIcon}
            color="bg-green-50 text-green-600"
          />
          <MetricCard
            title="Response Time"
            value={`${Math.round(metricsData.average_response_time)} days`}
            icon={ClockIcon}
            color="bg-purple-50 text-purple-600"
          />
          <MetricCard
            title="Active Opportunities"
            value={metricsData.interviews - metricsData.offers}
            icon={UserGroupIcon}
            color="bg-indigo-50 text-indigo-600"
          />
        </motion.div>

        <div className="grid grid-cols-12 gap-6">
          {/* Left Column - Analytics */}
          <motion.div 
            className="col-span-12 lg:col-span-8 space-y-6"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.3 }}
          >
            {/* Success Pipeline */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <ChartBarIcon className="h-6 w-6 text-blue-500" />
                  <h2 className="text-xl font-semibold">Your Success Journey</h2>
                </div>
                <select className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-sm">
                  <option>All Time</option>
                  <option>Past 90 Days</option>
                  <option>This Year</option>
                </select>
              </div>
              <ConversionMetrics 
                totalApplications={metricsData.total_applications}
                interviews={metricsData.interviews}
                offers={metricsData.offers}
              />
            </div>

            {/* Strategic Insights */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-3 mb-6">
                <CalendarIcon className="h-6 w-6 text-green-500" />
                <h2 className="text-xl font-semibold">Winning Strategies</h2>
              </div>
              <JobSearchGuidance stats={searchStats} />
            </div>
          </motion.div>

          {/* Right Column - Details & Progress */}
          <motion.div 
            className="col-span-12 lg:col-span-4 space-y-6"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.4 }}
          >
            {/* Progress Snapshot */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-3 mb-6">
                <BriefcaseIcon className="h-6 w-6 text-purple-500" />
                <h2 className="text-xl font-semibold">Progress Pulse</h2>
              </div>
              <div className="space-y-6">
                <div className="relative">
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600">Market Impact</span>
                    <span className="text-xl font-bold">
                      {formatPercentage(metricsData.engagement_rate)}
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                    <div 
                      className="bg-purple-500 h-2.5 rounded-full transition-all duration-500" 
                      style={{ 
                        width: `${Math.min(100, metricsData.engagement_rate)}%`,
                        transition: 'width 1s ease-in-out'
                      }}
                    />
                  </div>
                </div>

                <div className="relative">
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600">Interview Excellence</span>
                    <span className="text-xl font-bold">
                      {formatPercentage(metricsData.application_success_rate)}
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                    <div 
                      className="bg-green-500 h-2.5 rounded-full transition-all duration-500" 
                      style={{ 
                        width: `${Math.min(100, metricsData.application_success_rate)}%`,
                        transition: 'width 1s ease-in-out'
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Most Active Period */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold mb-4">Peak Performance Period</h3>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">
                    {new Date(additionalMetrics.mostActiveMonth.month).toLocaleDateString('default', { 
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                  <p className="text-2xl font-bold mt-1">
                    {additionalMetrics.mostActiveMonth.count} Career Moves
                  </p>
                </div>
                <div className="h-16 w-16 bg-blue-50 rounded-full flex items-center justify-center">
                  <ArrowTrendingUpIcon className="h-8 w-8 text-blue-500" />
                </div>
              </div>
            </div>

            {/* Target Positions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-3 mb-4">
                <BriefcaseIcon className="h-6 w-6 text-blue-500" />
                <h2 className="text-lg font-semibold">Dream Roles</h2>
              </div>
              <div className="space-y-2">
                {searchStats.mostCommonRoles.map((role, index) => (
                  <motion.div 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    key={role}
                    className="px-4 py-3 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg text-sm font-medium text-blue-800"
                  >
                    {role}
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ReportingPage;